import React, { useState } from "react";
import { User, ChevronDown, LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSuperAdminAuth } from "../../../hooks/useSuperAdminAuth";
import { useAppDispatch } from "../../../hooks/useStore";
import { logoutSuperAdmin } from "../../../store/slices/superAdminAuthSlice";
import LanguageSwitcher from "../../UI/LanguageSwitcher";
import { useTranslation } from "react-i18next";

interface SuperAdminNavbarProps {
  onToggleSidebar: () => void;
  isSidebarCollapsed: boolean;
  isMobile: boolean;
}

const SuperAdminNavbar: React.FC<SuperAdminNavbarProps> = ({
  onToggleSidebar,
  isMobile,
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { superAdmin } = useSuperAdminAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation("navigation");

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await dispatch(logoutSuperAdmin()).unwrap();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <header className="bg-[#FAFAF8] border-b border-[rgba(11,15,14,0.08)] shrink-0">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="p-2 text-[#8A9590] hover:text-[#C97A3C] hover:bg-[#F5F4F0] rounded-lg transition-colors lg:hidden"
              aria-label={t("sidebar.toggleSidebar")}
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] rounded-lg transition-colors"
              aria-label={t("user.userMenu")}
              aria-expanded={isUserMenuOpen}
            >
              <User className="h-5 w-5" />
              <span className="font-medium hidden sm:inline">
                {superAdmin?.firstName} {superAdmin?.lastName}
              </span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-[rgba(11,15,14,0.1)] py-2 z-50">
                <div className="px-4 py-3 border-b border-[rgba(11,15,14,0.06)]">
                  <p className="font-medium text-[#0B0F0E]">
                    {superAdmin?.firstName} {superAdmin?.lastName}
                  </p>
                  <p className="text-sm text-[#8A9590]">{superAdmin?.email}</p>
                  <p className="text-xs text-[#C97A3C] mt-1 font-medium">
                    {t("user.superAdministrator")}
                  </p>
                </div>

                <button
                  onClick={handleProfileClick}
                  className="flex items-center space-x-2 px-4 py-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] w-full text-left transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>{t("user.profileSettings")}</span>
                </button>

                <hr className="my-2 border-[rgba(11,15,14,0.06)]" />

                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 text-[#0B0F0E] hover:text-[#C97A3C] hover:bg-[#F5F4F0] w-full text-left transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("user.signOut")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default SuperAdminNavbar;
